'use client'

import { useEffect, useRef, useState } from 'react'

const PHASE1_TARGET = 85
const PHASE1_DURATION_MS = 600
const PHASE2_ASYMPTOTE = 99
const PHASE2_RANGE = PHASE2_ASYMPTOTE - PHASE1_TARGET
const PHASE2_TAU_MS = 29_400 / Math.log(7)
const TICK_MS = 32
const COMPLETE_ANIMATION_MS = 350
const FADE_OUT_MS = 300

type FakeProgressPhase = 'idle' | 'loading' | 'completing' | 'fading'

export function computeFakeProgress(elapsedMs: number) {
  if (elapsedMs <= PHASE1_DURATION_MS) {
    return PHASE1_TARGET * (elapsedMs / PHASE1_DURATION_MS)
  }

  const elapsedPhase2 = elapsedMs - PHASE1_DURATION_MS

  return PHASE1_TARGET + PHASE2_RANGE * (1 - Math.exp(-elapsedPhase2 / PHASE2_TAU_MS))
}

export function useFakeProgress(isLoading: boolean) {
  const [progress, setProgress] = useState(0)
  const [phase, setPhase] = useState<FakeProgressPhase>('idle')
  const wasLoadingRef = useRef(false)
  const loadingStartedAtRef = useRef(0)

  useEffect(() => {
    if (isLoading) {
      wasLoadingRef.current = true
      loadingStartedAtRef.current = performance.now()
      setPhase('loading')
      setProgress(0)

      const tick = () => {
        const elapsed = performance.now() - loadingStartedAtRef.current
        setProgress(computeFakeProgress(elapsed))
      }

      tick()
      const interval = setInterval(tick, TICK_MS)

      return () => clearInterval(interval)
    }

    if (!wasLoadingRef.current) return

    wasLoadingRef.current = false
    setPhase('completing')
    setProgress(100)

    const fadeTimer = setTimeout(() => setPhase('fading'), COMPLETE_ANIMATION_MS)
    const idleTimer = setTimeout(() => {
      setPhase('idle')
      setProgress(0)
    }, COMPLETE_ANIMATION_MS + FADE_OUT_MS)

    return () => {
      clearTimeout(fadeTimer)
      clearTimeout(idleTimer)
    }
  }, [isLoading])

  return {
    progress,
    showProgress: phase !== 'idle',
    isFadingOut: phase === 'fading',
    blocksInteraction: phase !== 'idle',
  }
}
